import React from 'react';
import { Paper, Typography, Box, Skeleton, useTheme, alpha, Avatar, Chip } from '@mui/material';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';

const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

export default function TopAgentsCard({ agentData, loading, error }) {
    const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);
    const theme = useTheme();

    return (
        <Paper elevation={0} sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            p: 3,
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}` },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                }}>
                    <EmojiEventsRoundedIcon />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Top Agents by Chats (MTD)
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={36} sx={{ borderRadius: 2 }} />
                    ))}
                </Box>
            ) : error ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Data not available right now, please try again later.
                    </Typography>
                </Box>
            ) : agentData && agentData.length > 0 ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {agentData.slice(0, 5).map((agent, index) => (
                        <Box key={agent.agentId || index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1,
                            px: 1.5,
                            borderRadius: 2,
                            bgcolor: index === 0 ? alpha(theme.palette.warning.main, 0.06) : 'transparent',
                            transition: 'background-color 0.15s',
                            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.06) },
                        }}>
                            <Avatar sx={{
                                width: 28,
                                height: 28,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: index < 3 ? alpha(rankColors[index], 0.2) : alpha(theme.palette.text.secondary, 0.1),
                                color: index < 3 ? rankColors[index] : theme.palette.text.secondary,
                            }}>
                                {index + 1}
                            </Avatar>
                            <Typography variant="body2" sx={{
                                flex: 1,
                                fontWeight: index === 0 ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {agent.agentName || 'Unknown Agent'}
                            </Typography>
                            <Chip
                                label={formatNumber(agent.chatCount || 0)}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No agent data available
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}
